import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  SettingOutlined,
  TeamOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  BellOutlined,
  GiftOutlined,
  BranchesOutlined,
  CalendarOutlined,
  InboxOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router';
import { useGetIdentity } from '@refinedev/core';
import { User } from '@src/types';
import { SidebarLogo } from '@src/components/Layout/Sidebar/SidebarLogo';
import { ProfileMenu } from '@src/components/Layout/Sidebar/ProfileMenu';
import { useSidebar } from '@src/components/Layout/SidebarContext';
import { RESTRICTED_ALLOWED_EMAILS } from '@src/routes/restrictedEmails';
import { canAccessSidebarItem } from '@src/utils/userAccess';


const { Sider } = Layout;

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [localCollapsed, setLocalCollapsed] = useState(collapsed);
  const [isMobile, setIsMobile] = useState(false);
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const {
    token: {
      colorBgContainer,
      borderRadiusLG,
      colorPrimary,
      colorTextSecondary,
    },
  } = theme.useToken();

  // Get current user identity for access control
  const { data: user } = useGetIdentity<User>();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Don't auto-collapse on mobile - keep it always open
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCollapse = (collapsed: boolean) => {
    setLocalCollapsed(collapsed);
    onCollapse?.(collapsed);
  };

  // Restricted pages (Reports + Payments) allowed only for specific emails
  const canAccessRestrictedPages =
    RESTRICTED_ALLOWED_EMAILS.includes((user?.email || '').trim().toLowerCase());

  // Define all possible menu items
  const allMenuItems = [
    {
      key: '/reports',
      icon: <DashboardOutlined style={{ fontSize: '16px' }} />,
      label: 'Reports',
      access: 'restricted_only', // Only allowed emails (checked below)
    },
    {
      key: '/organizations',
      icon: <ShoppingOutlined style={{ fontSize: '16px' }} />,
      label: 'Organizations',
      access: 'master_admin', // Only MASTER_ADMIN can access
    },
    // {
    //   key: '/branches',
    //   icon: <BranchesOutlined style={{ fontSize: '16px' }} />,
    //   label: 'Branches',
    //   access: 'all', // All users can access
    // },
    {
      key: '/offerings',
      icon: <GiftOutlined style={{ fontSize: '16px' }} />,
      label: 'Offerings',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/users',
      icon: <TeamOutlined style={{ fontSize: '16px' }} />,
      label: 'Users',
      access: 'admin_plus', // Only MASTER_ADMIN, ADMIN, and DOCTOR can access
    },
    {
      key: '/appointments',
      icon: <BellOutlined style={{ fontSize: '16px' }} />,
      label: 'Appointments',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/leads',
      icon: <FileTextOutlined style={{ fontSize: '16px' }} />,
      label: 'Leads',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/patients',
      icon: <UserOutlined style={{ fontSize: '16px' }} />,
      label: 'Patients',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/medical',
      icon: <BarChartOutlined style={{ fontSize: '16px' }} />,
      label: 'Medical',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/inside-stock',
      icon: <InboxOutlined style={{ fontSize: '16px' }} />,
      label: 'Inside Stock',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
    {
      key: '/payments',
      icon: <CreditCardOutlined style={{ fontSize: '16px' }} />,
      label: 'Payments',
      access: 'restricted_only', // Only allowed emails (checked below)
    },
    {
      key: '/tasks',
      icon: <CalendarOutlined style={{ fontSize: '16px' }} />,
      label: 'Tasks',
      access: 'non_master_admin', // All users except MASTER_ADMIN can access
    },
  ];

  // Filter menu items based on user access
  const menuItems = allMenuItems.filter(item =>
    canAccessSidebarItem(user?.type, item.key, canAccessRestrictedPages)
  );

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <>
      {/* Mobile overlay for click outside */}
      {isMobile && sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
          onClick={() => {
            // Close sidebar when clicking outside
            setSidebarOpen(false);
          }}
        />
      )}
      
      <Sider
        trigger={null}
        collapsible
        collapsed={localCollapsed}
        style={{
          background: '#ffffff',
          borderRight: '1px solid #f0f0f0',
          boxShadow: isMobile ? '2px 0 8px rgba(0, 0, 0, 0.15)' : '2px 0 4px rgba(0, 0, 0, 0.06)',
          position: isMobile ? 'fixed' : 'relative',
          height: isMobile ? '100vh' : 'calc(100vh - 40px)',
          overflow: 'hidden',
          marginTop: isMobile ? '0' : '20px',
          borderBottomRightRadius: isMobile ? '0' : '12px',
          borderTopRightRadius: isMobile ? '0' : '12px',
          zIndex: isMobile ? 1000 : 'auto',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? (sidebarOpen ? '0' : '-220px') : (localCollapsed ? '-220px' : '0'),
          transition: isMobile ? 'left 0.3s ease' : 'none',
        }}
        width={220}
      >

      {/* Logo Section */}
      <SidebarLogo collapsed={localCollapsed} />

      {/* Menu Section */}
      <div
        style={{
          // overflow: 'auto',
          paddingTop: '8px',
          paddingBottom: '8px',
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: '14px',
          }}
          expandIcon={({ isOpen }) => (
            <span
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                display: 'inline-block',
                fontSize: '10px',
                color: colorTextSecondary,
              }}
            >
              ▶
            </span>
          )}
        />
      </div>

      {/* Bottom Section - Profile Menu and Collapse Button */}
      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          background: '#fafafa',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          height: '160px',
        }}
      >
        {/* Profile Menu */}
        <ProfileMenu collapsed={localCollapsed} />

        {/* Collapse/Expand Button */}
        <Button
          type="text"
          icon={localCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => handleCollapse(!localCollapsed)}
          style={{
            fontSize: '14px',
            height: '36px',
            width: '100%',
            background: '#ffffff',
            border: '1px solid #e8e8e8',
            borderRadius: '6px',
            color: colorTextSecondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = colorPrimary;
            e.currentTarget.style.color = colorPrimary;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#e8e8e8';
            e.currentTarget.style.color = colorTextSecondary;
          }}
        >
          {!localCollapsed && 'Collapse Menu'}
        </Button>
      </div>
    </Sider>
    </>
  );
};
